/**
 * Match accounts list page.
 *
 * Lists every `match_account` with its current balance, status, and optional
 * linked main-ledger party. Supports a status filter (active / archived /
 * all), creating a new account via an inline dialog, and PIN-gated archive /
 * unarchive actions. Balances are rendered as-is from the server (already a
 * canonical Decimal string in K-INR) — no arithmetic on the client.
 *
 * Layout:
 *   Desktop (>= md): header row + full table with inline actions.
 *   Mobile (< md):   same header, table collapses to stacked cards that are
 *                    tappable to navigate into the detail page.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { FolderPlus, Plus } from 'lucide-react';
import {
  api,
  ApiError,
  match,
  type MatchAccount,
  type Party,
} from '../api';
import { MatchShell } from './MatchShell';
import { usePinGate } from '../auth/pinGate';
import {
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  Pill,
  Select,
  Spinner,
  TextArea,
  TextInput,
} from '../ui/primitives';

type StatusFilter = 'active' | 'archived' | 'all';

export function MatchAccountsPage() {
  const [status, setStatus] = useState<StatusFilter>('active');
  const [createOpen, setCreateOpen] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);

  const qc = useQueryClient();
  const { withPin } = usePinGate();
  const nav = useNavigate();

  const accountsQ = useQuery<MatchAccount[]>({
    queryKey: ['match-accounts', status],
    queryFn: () => match.listAccounts({ status }),
  });

  const balancesQ = useQuery<Array<{ matchAccountId: number; balance: string }>>({
    queryKey: ['match-balances'],
    queryFn: () => match.listBalances(),
  });

  const partiesQ = useQuery<Party[]>({
    queryKey: ['parties', 'all'],
    queryFn: () => api.get<Party[]>('/api/parties'),
  });

  const partiesById = useMemo(
    () => new Map((partiesQ.data ?? []).map((p) => [p.id, p])),
    [partiesQ.data],
  );

  const balancesById = useMemo(
    () =>
      new Map(
        (balancesQ.data ?? []).map((b) => [b.matchAccountId, b.balance]),
      ),
    [balancesQ.data],
  );

  const invalidateLists = () => {
    void qc.invalidateQueries({ queryKey: ['match-accounts'] });
    void qc.invalidateQueries({ queryKey: ['match-balances'] });
  };

  const archiveMut = useMutation({
    mutationFn: (id: number) => match.archiveAccount(id),
    onSuccess: () => {
      setPageError(null);
      invalidateLists();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 423) return; // PIN gate re-runs
      setPageError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const unarchiveMut = useMutation({
    mutationFn: (id: number) => match.unarchiveAccount(id),
    onSuccess: () => {
      setPageError(null);
      invalidateLists();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 423) return;
      setPageError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const onArchive = (id: number) => {
    void withPin(() => archiveMut.mutateAsync(id));
  };
  const onUnarchive = (id: number) => {
    void withPin(() => unarchiveMut.mutateAsync(id));
  };

  const loading = accountsQ.isLoading || balancesQ.isLoading;
  const accounts = accountsQ.data ?? [];
  const isEmpty = !loading && accounts.length === 0;

  return (
    <MatchShell>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-ink sm:text-2xl">
              Match Accounts
            </h1>
            <p className="mt-1 text-xs text-ink-muted sm:text-sm">
              First-class accounts used for the per-match P&amp;L flow. Linked
              parties are display-only — balances stay isolated from the main
              ledger.
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => {
              setPageError(null);
              setCreateOpen(true);
            }}
          >
            <Plus className="h-4 w-4" strokeWidth={2} />
            <span>New Account</span>
          </Button>
        </div>

        {/* Status filter (segmented) */}
        <StatusSegmented value={status} onChange={setStatus} />

        {pageError && <ErrorBanner>{pageError}</ErrorBanner>}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-ink-muted">
            <Spinner /> Loading match accounts…
          </div>
        ) : isEmpty ? (
          <EmptyState
            icon={FolderPlus}
            title={
              status === 'active'
                ? 'No active match accounts yet'
                : status === 'archived'
                ? 'No archived accounts'
                : 'No match accounts yet'
            }
            description={
              status === 'active'
                ? 'Create your first match account to start recording per-match entries.'
                : 'Switch to Active to see the accounts you use daily.'
            }
            action={
              status !== 'archived' ? (
                <Button size="sm" onClick={() => setCreateOpen(true)}>
                  <Plus className="h-4 w-4" strokeWidth={2} />
                  <span>Create first account</span>
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden overflow-hidden rounded-card border border-border bg-surface shadow-card md:block">
              <table className="min-w-full text-sm">
                <thead className="bg-surface-raised">
                  <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-ink-muted">
                    <th className="px-3 py-2.5">Name</th>
                    <th className="px-3 py-2.5 text-right">Balance</th>
                    <th className="px-3 py-2.5">Linked party</th>
                    <th className="px-3 py-2.5">Status</th>
                    <th className="px-3 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {accounts.map((a) => {
                    const linked =
                      a.linkedPartyId !== null
                        ? partiesById.get(a.linkedPartyId)
                        : null;
                    const bal = balancesById.get(a.id) ?? '0';
                    const busy =
                      (archiveMut.isPending &&
                        archiveMut.variables === a.id) ||
                      (unarchiveMut.isPending &&
                        unarchiveMut.variables === a.id);
                    return (
                      <tr
                        key={a.id}
                        className="transition-colors hover:bg-surface-raised/60"
                      >
                        <td className="px-3 py-2.5">
                          <Link
                            to={`/match/accounts/${a.id}`}
                            className="font-medium text-brand hover:underline"
                          >
                            {a.name}
                          </Link>
                          {a.notes && (
                            <div className="mt-0.5 line-clamp-1 text-[11px] text-ink-muted">
                              {a.notes}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono text-ink">
                          {bal}
                        </td>
                        <td className="px-3 py-2.5 text-ink">
                          {linked ? (
                            <span>
                              {linked.displayName}
                              <span className="ml-1 text-[10px] uppercase text-ink-muted">
                                {linked.role}
                              </span>
                            </span>
                          ) : (
                            <span className="text-ink-muted">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <Pill
                            tone={a.status === 'active' ? 'green' : 'neutral'}
                          >
                            {a.status}
                          </Pill>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => nav(`/match/accounts/${a.id}`)}
                            >
                              View
                            </Button>
                            {a.status === 'active' ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={busy}
                                onClick={() => onArchive(a.id)}
                              >
                                {busy ? <Spinner /> : 'Archive'}
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={busy}
                                onClick={() => onUnarchive(a.id)}
                              >
                                {busy ? <Spinner /> : 'Unarchive'}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="space-y-2 md:hidden">
              {accounts.map((a) => {
                const linked =
                  a.linkedPartyId !== null
                    ? partiesById.get(a.linkedPartyId)
                    : null;
                const bal = balancesById.get(a.id) ?? '0';
                const busy =
                  (archiveMut.isPending && archiveMut.variables === a.id) ||
                  (unarchiveMut.isPending &&
                    unarchiveMut.variables === a.id);
                return (
                  <AccountCard
                    key={a.id}
                    account={a}
                    linkedName={linked?.displayName ?? null}
                    balance={bal}
                    busy={busy}
                    onView={() => nav(`/match/accounts/${a.id}`)}
                    onArchive={() => onArchive(a.id)}
                    onUnarchive={() => onUnarchive(a.id)}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>

      {createOpen && (
        <CreateMatchAccountDialog
          parties={partiesQ.data ?? []}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            invalidateLists();
          }}
        />
      )}
    </MatchShell>
  );
}

// ---------------------------------------------------------------------------
// Internal bits
// ---------------------------------------------------------------------------

function StatusSegmented({
  value,
  onChange,
}: {
  value: StatusFilter;
  onChange: (v: StatusFilter) => void;
}) {
  const options: { value: StatusFilter; label: string }[] = [
    { value: 'active', label: 'Active' },
    { value: 'archived', label: 'Archived' },
    { value: 'all', label: 'All' },
  ];
  return (
    <div
      role="tablist"
      aria-label="Filter by status"
      className="inline-flex rounded-xl border border-border bg-surface-raised p-1"
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.value)}
            className={[
              'min-h-[36px] rounded-lg px-3 py-1 text-xs font-medium transition-colors',
              active
                ? 'bg-brand text-white'
                : 'text-ink-muted hover:text-ink',
            ].join(' ')}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function AccountCard({
  account,
  linkedName,
  balance,
  busy,
  onView,
  onArchive,
  onUnarchive,
}: {
  account: MatchAccount;
  linkedName: string | null;
  balance: string;
  busy: boolean;
  onView: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
}) {
  return (
    <Card className="!p-0">
      <button
        type="button"
        onClick={onView}
        className="flex w-full items-start justify-between gap-3 px-3 py-3 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-ink">
              {account.name}
            </span>
            <Pill tone={account.status === 'active' ? 'green' : 'neutral'}>
              {account.status}
            </Pill>
          </div>
          <dl className="mt-2 space-y-0.5 text-[11px]">
            <div className="flex justify-between gap-2">
              <dt className="text-ink-muted">Balance</dt>
              <dd className="font-mono text-ink">{balance}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-ink-muted">Linked party</dt>
              <dd className="truncate text-ink">
                {linkedName ?? (
                  <span className="text-ink-muted">—</span>
                )}
              </dd>
            </div>
          </dl>
          {account.notes && (
            <p className="mt-2 line-clamp-2 text-[11px] text-ink-muted">
              {account.notes}
            </p>
          )}
        </div>
      </button>
      <div className="flex gap-2 border-t border-border px-3 py-2">
        <Button
          size="sm"
          variant="ghost"
          className="flex-1 justify-center"
          onClick={onView}
        >
          View
        </Button>
        {account.status === 'active' ? (
          <Button
            size="sm"
            variant="ghost"
            className="flex-1 justify-center"
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              onArchive();
            }}
          >
            {busy ? <Spinner /> : 'Archive'}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            className="flex-1 justify-center"
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              onUnarchive();
            }}
          >
            {busy ? <Spinner /> : 'Unarchive'}
          </Button>
        )}
      </div>
    </Card>
  );
}

function CreateMatchAccountDialog({
  parties,
  onClose,
  onCreated,
}: {
  parties: Party[];
  onClose: () => void;
  onCreated: (created: MatchAccount) => void;
}) {
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [linkedPartyId, setLinkedPartyId] = useState<string>(''); // '' = none
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () =>
      match.createAccount({
        name: name.trim(),
        notes: notes.trim().length === 0 ? null : notes.trim(),
        linkedPartyId:
          linkedPartyId === '' ? null : Number(linkedPartyId),
      }),
    onSuccess: (created) => {
      onCreated(created);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (name.trim().length === 0) {
      setError('Name is required.');
      return;
    }
    createMut.mutate();
  };

  const partyOptions = [
    { value: '', label: '— none —' },
    ...parties.map((p) => ({
      value: String(p.id),
      label: `${p.displayName} (${p.role})`,
    })),
  ];

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-ink/50 backdrop-blur-sm sm:items-center sm:p-4">
      <div className="flex w-full max-w-xl flex-col overflow-hidden rounded-t-2xl border border-border bg-surface shadow-elevated sm:rounded-xl">
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-base font-semibold text-ink">New match account</h2>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Close
          </Button>
        </header>
        <form className="overflow-y-auto p-4" onSubmit={onSubmit}>
          <div className="space-y-3">
            <TextInput
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              name="name"
              placeholder="e.g. Shared-paati"
              hint="Case-insensitive unique across all match accounts."
              autoFocus
            />
            <Select
              label="Linked party (optional)"
              value={linkedPartyId}
              onChange={(v) => setLinkedPartyId(v)}
              options={partyOptions}
              hint="Display-only link to a main-ledger party. Does not affect balances."
            />
            <TextArea
              label="Notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional description or context"
              hint="Metadata only."
            />
          </div>

          {error && (
            <div className="mt-3">
              <ErrorBanner>{error}</ErrorBanner>
            </div>
          )}

          <div className="mt-4 flex items-center gap-2">
            <Button type="submit" disabled={createMut.isPending}>
              {createMut.isPending ? <Spinner /> : 'Create account'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={createMut.isPending}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
